import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShoppingCart, Users, Package, AlertTriangle, ActivitySquare, Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { LineChartComponent, PieChartComponent } from '../components/CustomChart';
import DataTable from '../components/DataTable';
import { apiService } from '../services/api';
import { api, productApi } from '../../services/api';
import {
  dashboardStats,
  analyticsData,
  recentOrdersForDashboard,
  topProductsForDashboard,
  lowStockProducts,
  activityLogs,
} from '../data/dummyData';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: dashboardStats.totalRevenue,
    totalOrders: dashboardStats.totalOrders,
    totalCustomers: dashboardStats.totalCustomers,
    totalProducts: dashboardStats.totalProducts,
  });
  const [orders, setOrders] = useState(recentOrdersForDashboard);
  const [products, setProducts] = useState(topProductsForDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const dashboardRes = await apiService.getDashboard();
        const dashboard = dashboardRes?.data || {};

        // Load products
        const productsRes = await productApi.list({ limit: 100 });
        const productsList = (productsRes.data?.data || []).slice(0, 10);
        setProducts(productsList.map(p => ({
          id: p._id,
          name: p.name,
          price: p.price,
          stock: p.stock,
          salesThisMonth: Math.floor(Math.random() * 50),
          rating: Math.round(Math.random() * 5 * 10) / 10
        })));

        // Load orders
        const ordersRes = await api.get('/orders');
        const ordersList = (ordersRes.data?.data || []).slice(0, 10);
        setOrders(ordersList.map(o => ({
          id: o._id || o.id,
          customerName: o.userId?.name || o.customerName || 'Customer',
          amount: o.totalAmount || o.amount || 0,
          status: o.orderStatus || o.status || 'Pending',
          date: new Date(o.createdAt || Date.now()).toLocaleDateString()
        })));

        setStats({
          totalRevenue: dashboard.revenue || 0,
          totalOrders: dashboard.totalOrders || ordersList.length,
          totalCustomers: dashboard.users || 0,
          totalProducts: dashboard.products || productsList.length
        });
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const statCards = [
    {
      title: 'Revenue',
      value: `₹${(stats.totalRevenue / 100000).toFixed(2)}L`,
      icon: TrendingUp,
      trend: dashboardStats.totalRevenueChange,
      bgColor: 'bg-gradient-to-br from-[#f4e8d3] to-[#d8bb92]',
    },
    {
      title: 'Orders',
      value: stats.totalOrders,
      icon: ShoppingCart,
      trend: dashboardStats.totalOrdersChange,
      bgColor: 'bg-gradient-to-br from-[#ede2d4] to-[#b68d60]',
    },
    {
      title: 'Customers',
      value: stats.totalCustomers,
      icon: Users,
      trend: dashboardStats.totalCustomersChange,
      bgColor: 'bg-gradient-to-br from-[#f4ede4] to-[#a27d5f]',
    },
    {
      title: 'Products',
      value: stats.totalProducts,
      icon: Package,
      trend: dashboardStats.totalProductsChange,
      bgColor: 'bg-gradient-to-br from-[#f7efdf] to-[#c28f52]',
    },
  ];

  const orderColumns = [
    { key: 'id', label: 'Order ID', sortable: true },
    { key: 'customerName', label: 'Customer', sortable: true },
    { key: 'amount', label: 'Amount', render: (val) => `₹${Number(val || 0).toLocaleString()}`, sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'delivered'
            ? 'bg-emerald-100 text-emerald-700'
            : val === 'shipped'
            ? 'bg-sky-100 text-sky-700'
            : 'bg-amber-100 text-amber-700'
        }`}>
          {String(val || 'pending').charAt(0).toUpperCase() + String(val || 'pending').slice(1)}
        </span>
      ),
    },
    { key: 'date', label: 'Date', sortable: true },
  ];

  const productColumns = [
    { key: 'name', label: 'Product', sortable: true },
    { key: 'price', label: 'Price', render: (val) => `₹${Number(val || 0).toLocaleString()}`, sortable: true },
    { key: 'stock', label: 'Stock', sortable: true },
    { key: 'salesThisMonth', label: 'Sales', sortable: true },
    {
      key: 'rating',
      label: 'Rating',
      render: (val) => (
        <div className="flex items-center gap-1 text-amber-600">
          <span>★</span>
          <span className="font-semibold">{Number(val || 0).toFixed(1)}</span>
        </div>
      ),
    },
  ];

  const lowStockColumns = [
    { key: 'name', label: 'Product', sortable: true },
    { key: 'stock', label: 'Current Stock', sortable: true },
    { key: 'threshold', label: 'Threshold', sortable: true },
    {
      key: 'stock',
      label: 'Status',
      render: (val, row) => (
        <span className="flex items-center gap-1 text-red-600 text-xs sm:text-sm">
          <AlertTriangle size={14} />
          {(row.threshold || 10) - (val || 0)} below threshold
        </span>
      ),
    },
  ];

  const activityColumns = [
    { key: 'action', label: 'Action', sortable: false },
    { key: 'detail', label: 'Detail', sortable: false },
    { key: 'user', label: 'User', sortable: true },
    { key: 'timestamp', label: 'Time', sortable: true },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      <PageHeader
        title="Dashboard"
        description="Premium furniture ecommerce insights for your Fosen brand."
        breadcrumbs={['Home', 'Dashboard']}
      />

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-xs sm:text-sm text-yellow-700">
          {error} - Showing sample data
        </div>
      )}

      {/* Snapshot and Stat Cards Grid */}
      <div className="grid gap-6 xl:grid-cols-[1.8fr_1fr]">
        <div className="rounded-2xl sm:rounded-[32px] border border-[#e4d9c4] bg-[#fff7ec] p-4 sm:p-6 shadow-[0_30px_80px_rgba(149,116,68,0.08)]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#8b6f53]">Revenue Snapshot</p>
              <h2 className="mt-1 sm:mt-2 text-xl sm:text-3xl font-semibold text-[#2e220f]">Luxury sales performance</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
              <div className="rounded-2xl sm:rounded-3xl bg-[#fff0dc] px-3.5 sm:px-4 py-3 sm:py-4">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#8b6f53]">Today</p>
                <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-[#3e2c1f]">₹1.2L</p>
              </div>
              <div className="rounded-2xl sm:rounded-3xl bg-[#f3ede2] px-3.5 sm:px-4 py-3 sm:py-4">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#8b6f53]">This Week</p>
                <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-[#3e2c1f]">₹7.8L</p>
              </div>
              <div className="rounded-2xl sm:rounded-3xl bg-[#ece1ce] px-3.5 sm:px-4 py-3 sm:py-4">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#8b6f53]">Growth</p>
                <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-[#3e2c1f]">+18.4%</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="mt-6 flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-amber-600" size={24} />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4">
              {statCards.map((stat, idx) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <StatCard {...stat} />
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Charts Column */}
        <div className="space-y-6">
          <div className="rounded-2xl sm:rounded-[32px] border border-[#e4d9c4] bg-[#fff7ec] p-4 sm:p-6 shadow-[0_25px_60px_rgba(149,116,68,0.08)]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
              <div>
                <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8b6f53]">Top KPI</p>
                <h3 className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-[#2e220f]">Orders Conversion</h3>
              </div>
              <span className="self-start sm:self-auto rounded-full bg-[#f8dcc4] px-3 py-1 text-[11px] sm:text-xs font-semibold text-[#7e5f3d]">Premium traffic</span>
            </div>
            <div className="mt-4">
              <LineChartComponent data={analyticsData.monthlySales} dataKey="revenue" title="Sales Analytics" xAxisKey="month" />
            </div>
          </div>

          <div className="rounded-2xl sm:rounded-[32px] border border-[#e4d9c4] bg-[#fff7ec] p-4 sm:p-6 shadow-[0_25px_60px_rgba(149,116,68,0.08)]">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8b6f53]">Status</p>
            <h3 className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-[#2e220f]">Order Breakdown</h3>
            <div className="mt-5">
              <PieChartComponent data={analyticsData.orderStatus} dataKey="value" title="Order Status" />
            </div>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr] w-full min-w-0 max-w-full">
        <motion.div className="w-full min-w-0 max-w-full overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <DataTable
            title="Recent Orders"
            columns={orderColumns}
            data={orders}
            searchable={true}
            paginate={false}
          />
        </motion.div>

        <div className="space-y-6 w-full min-w-0 max-w-full overflow-hidden">
          <motion.div className="w-full min-w-0 max-w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <div className="rounded-2xl sm:rounded-[32px] border border-[#e4d9c4] bg-[#fff7ec] p-4 sm:p-6 shadow-[0_25px_60px_rgba(149,116,68,0.08)] w-full min-w-0 max-w-full overflow-hidden">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl sm:rounded-3xl bg-[#d8b48b] p-2.5 sm:p-3 text-[#452c16]">
                  <AlertTriangle size={18} className="sm:w-5 sm:h-5" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8b6f53]">Low Stock</p>
                  <h3 className="mt-1 text-lg sm:text-xl font-semibold text-[#2e220f]">Restock essential pieces</h3>
                </div>
              </div>
              <div className="mt-4 sm:mt-6 w-full min-w-0 max-w-full overflow-x-auto scrollbar-thin">
                <DataTable
                  columns={lowStockColumns}
                  data={lowStockProducts}
                  searchable={false}
                  paginate={false}
                />
              </div>
            </div>
          </motion.div>

          <motion.div className="w-full min-w-0 max-w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <div className="rounded-2xl sm:rounded-[32px] border border-[#e4d9c4] bg-[#fff7ec] p-4 sm:p-6 shadow-[0_25px_60px_rgba(149,116,68,0.08)] w-full min-w-0 max-w-full overflow-hidden">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl sm:rounded-3xl bg-[#d8b48b] p-2.5 sm:p-3 text-[#452c16]">
                  <ActivitySquare size={18} className="sm:w-5 sm:h-5" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8b6f53]">Activity</p>
                  <h3 className="mt-1 text-lg sm:text-xl font-semibold text-[#2e220f]">Recent Updates</h3>
                </div>
              </div>
              <div className="mt-4 sm:mt-6 w-full min-w-0 max-w-full overflow-x-auto scrollbar-thin">
                <DataTable
                  columns={activityColumns}
                  data={activityLogs}
                  searchable={false}
                  paginate={false}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div className="w-full min-w-0 max-w-full overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        <DataTable
          title="Top Products"
          columns={productColumns}
          data={products}
          searchable={true}
          paginate={false}
        />
      </motion.div>
    </div>
  );
};

export default Dashboard;
